import { useSuperadmin } from '@/hooks/useSuperadmin';
import { api } from '@/lib/api';
import type { AdminUsersResponse, AdminUser, Event, Purchase } from '@eventflow/shared-types';
import { useAppSelector } from '@/store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  id: string;
  type: 'event' | 'user' | 'attendee';
  title: string;
  subtitle: string;
  url: string;
  icon: React.ReactNode;
}

const RECENT_SEARCHES_KEY_PREFIX = 'eventflow:global-search-recent';

type ActiveFilter = 'all' | 'event' | 'attendee' | 'user';
type SortOrder = 'relevance' | 'az' | 'za';

const organizerFilterOptions: { id: ActiveFilter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'event', label: 'Eventos' },
  { id: 'attendee', label: 'Asistentes / Compras' },
];

const superadminFilterOptions: { id: ActiveFilter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'user', label: 'Usuarios' },
];

const sortOptions: { id: SortOrder; label: string }[] = [
  { id: 'relevance', label: 'Relevancia' },
  { id: 'az', label: 'A-Z' },
  { id: 'za', label: 'Z-A' },
];

const icons = {
  search: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  ),
  event: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
      />
    </svg>
  ),
  user: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  ),
  ticket: (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z"
      />
    </svg>
  ),
  refresh: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 12a8 8 0 10-3 6.245M20 12v6m0-6h-6"
      />
    </svg>
  ),
  download: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v10m0 0 4-4m-4 4-4-4M4 17v3h16v-3"
      />
    </svg>
  ),
};

export default function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');        
  const [sortOrder, setSortOrder] = useState<SortOrder>('relevance');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isSuperadmin = useSuperadmin();
  const isSuperadminMode = isSuperadmin === true;
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const recentSearchesStorageKey = useMemo(() => {
    if (typeof window === 'undefined') return null;
    if (isSuperadmin === null) return null;
    if (!currentUserId) return null;
    const roleSuffix = isSuperadmin ? 'superadmin' : 'organizer';
    return `${RECENT_SEARCHES_KEY_PREFIX}:${currentUserId}:${roleSuffix}`;
  }, [currentUserId, isSuperadmin]);

  const activeFilterOptions = useMemo(
    () => (isSuperadminMode ? superadminFilterOptions : organizerFilterOptions),
    [isSuperadminMode]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!recentSearchesStorageKey) return;
    setRecentSearches([]);
    try {
      const stored = window.localStorage.getItem(recentSearchesStorageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        setRecentSearches(
          parsed
            .filter((item) => typeof item === 'string' && item.trim())
            .slice(0, 3)
        );
      }
    } catch (error) {
      console.error('Error al cargar búsquedas recientes:', error);
    }
  }, [recentSearchesStorageKey]);

  // Manejar atajo de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault(); // Prevenir comportamiento del navegador
        e.stopPropagation();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown); // Usar window en lugar de document para mayor seguridad
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input al abrir
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setActiveFilter('all');
      setSortOrder('relevance');
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!isOpen) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isOpen || !isSuperadminMode) return;
    window.dispatchEvent(
      new CustomEvent('users:search:set', {
        detail: { term: query },
      })
    );
  }, [query, isOpen, isSuperadminMode]);

  // Búsqueda con debounce
  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const searchTermLower = trimmedQuery.toLowerCase();
        const newResults: SearchResult[] = [];

        if (isSuperadminMode) {
          let users = adminUsers;
          if (!users) {
            const data = await api.get<AdminUsersResponse>('/users');
            users = data.users;
            setAdminUsers(users);
          }

          (users || [])
            .filter((user) => {
              const email = (user.email || '').toLowerCase();
              const name = (user.name || '').toLowerCase();
              const organization = (user.organization_name || '').toLowerCase();
              const phone = (user.phone || '').toLowerCase();
              const roles = (user.roles || []).join(' ').toLowerCase();
              return (
                email.includes(searchTermLower) ||
                name.includes(searchTermLower) ||
                organization.includes(searchTermLower) ||
                phone.includes(searchTermLower) ||
                roles.includes(searchTermLower)
              );
            })
            .slice(0, 8)
            .forEach((user) => {
              const rolesLabel = user.roles?.length ? user.roles.join(', ') : 'sin rol';
              const statusLabel = user.banned_until ? 'Desactivado' : 'Activo';
              const subtitleParts = [user.email, user.organization_name, rolesLabel, statusLabel].filter(
                Boolean
              );
              newResults.push({
                id: user.id,
                type: 'user',
                title: user.name || user.email,
                subtitle: subtitleParts.join(' - '),
                url: '/dashboard/users',
                icon: icons.user,
              });
            });

          setResults(newResults);
          return;
        }

        // Búsquedas paralelas usando la API del backend (organizer)
        const [eventsData, paymentsData] = await Promise.all([
          // 1. Buscar Eventos - usar endpoint existente
          api
            .get<Event[]>('/events')
            .catch(() => [] as Event[]),

          // 2. Buscar en Compras del organizador
          api
            .get<(Purchase & { event?: { name: string } })[]>('/purchases/organizer')
            .catch(() => [] as Purchase[]),
        ]);

        // Filtrar eventos por nombre (búsqueda client-side)
        if (eventsData && Array.isArray(eventsData)) {
          eventsData
            .filter(
              (event: Event) =>
                event.name.toLowerCase().includes(searchTermLower) ||
                event.venue_name.toLowerCase().includes(searchTermLower)
            )
            .slice(0, 5)
            .forEach((event: Event) => {
              newResults.push({
                id: event.id,
                type: 'event',
                title: event.name,
                subtitle: `${new Date(event.event_date).toLocaleDateString()} - ${event.venue_name}`,
                url: `/dashboard/events/${event.id}`,
                icon: icons.event,
              });
            });
        }

        // Filtrar asistentes/compras por nombre o email
        if (paymentsData && Array.isArray(paymentsData)) {
          paymentsData
            .filter(
              (purchase: Purchase & { event?: { name: string } }) =>
                purchase.customer_name?.toLowerCase().includes(searchTermLower) ||
                purchase.customer_email?.toLowerCase().includes(searchTermLower)
            )
            .slice(0, 5)
            .forEach((purchase: Purchase & { event?: { name: string } }) => {
              const eventName = purchase.event?.name || 'Evento';
              const attendeeTitle = purchase.customer_name || purchase.customer_email || 'Asistente';
              // Usar el nombre como término de búsqueda para filtrar automáticamente
              const searchTerm = encodeURIComponent(
                purchase.customer_name || purchase.customer_email || ''
              );
              newResults.push({
                id: purchase.id,
                type: 'attendee',
                title: attendeeTitle,
                subtitle: `${purchase.customer_email} - ${eventName}`,
                url: `/dashboard/payments?search=${searchTerm}&highlight=${purchase.id}`,
                icon: icons.ticket,
              });
            });
        }

        setResults(newResults);
      } catch (err) {
        console.error('Error en búsqueda global:', err);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, adminUsers, isSuperadminMode]);

  const saveRecentSearch = (term: string) => {
    const normalized = term.trim();
    if (!normalized) return;
    if (!recentSearchesStorageKey) return;
    setRecentSearches((prev) => {
      const next = [
        normalized,
        ...prev.filter((item) => item.toLowerCase() !== normalized.toLowerCase()),
      ].slice(0, 3);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(recentSearchesStorageKey, JSON.stringify(next));
        } catch (error) {
          console.error('Error al guardar búsquedas recientes:', error);
        }
      }
      return next;
    });
  };

  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(query.trim() || result.title);
    setIsOpen(false);

    if (isSuperadminMode && result.type === 'user') {
      navigate('/dashboard/users');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('users:open', { detail: { userId: result.id } }));
      }
      return;
    }

    navigate(result.url);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    if (typeof window !== 'undefined') {
      try {
        if (!recentSearchesStorageKey) return;
        window.localStorage.removeItem(recentSearchesStorageKey);
      } catch (error) {
        console.error('Error al limpiar búsquedas recientes:', error);
      }
    }
  };

  const triggerUsersCreate = () => {
    setIsOpen(false);
    navigate('/dashboard/users');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('users:open-create-modal'));
    }
  };

  const triggerUsersExport = () => {
    setIsOpen(false);
    navigate('/dashboard/users');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('users:export-csv'));
    }
  };

  const triggerUsersReload = () => {
    setAdminUsers(null);
    setResults([]);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('users:reload'));
    }
  };

  // Exponer función para abrir desde fuera
  useEffect(() => {
    const handleOpenSearch = () => setIsOpen(true);
    window.addEventListener('open-global-search', handleOpenSearch);
    return () => window.removeEventListener('open-global-search', handleOpenSearch);
  }, []);

  const sortResults = (items: SearchResult[]) => {
    if (sortOrder === 'relevance') return items;
    const sorted = [...items].sort((a, b) =>
      a.title.localeCompare(b.title, 'es', { sensitivity: 'base' })
    );
    return sortOrder === 'az' ? sorted : sorted.reverse();
  };

  const resultsByType = useMemo(
    () => ({
      event: sortResults(results.filter((result) => result.type === 'event')),
      attendee: sortResults(results.filter((result) => result.type === 'attendee')),
      user: sortResults(results.filter((result) => result.type === 'user')),
    }),
    [results, sortOrder]
  );

  const sections = useMemo(() => {
    if (isSuperadminMode) {
      return [{ id: 'user' as const, label: 'Usuarios', items: resultsByType.user }];
    }

    return [
      { id: 'event' as const, label: 'Eventos', items: resultsByType.event },
      { id: 'attendee' as const, label: 'Asistentes / Compras', items: resultsByType.attendee },
    ];
  }, [isSuperadminMode, resultsByType]);

  const visibleSections = sections.filter((section) => {
    if (activeFilter !== 'all' && section.id !== activeFilter) return false;
    return section.items.length > 0;
  });

  const hasQuery = query.trim().length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4 sm:p-6 md:p-10">
      {/* Backdrop Premium Glass */}
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity z-10"
        onClick={() => setIsOpen(false)}
      />

      {/* Modal Premium */}
      <div className="relative z-20 w-full max-w-3xl transform overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 transition-all">
        <div className="border-b border-gray-100/80 bg-gradient-to-b from-white via-white to-gray-50/80">
          <div className="flex items-center gap-3 px-5 pt-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              {icons.search}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                Búsqueda rápida
              </p>
              <p className="text-sm font-medium text-gray-900">Global</p>
            </div>
            <div className="ml-auto hidden items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-[11px] font-medium text-gray-400 sm:flex">
              <span>Ctrl</span>
              <span>K</span>
            </div>
          </div>

          <div className="relative px-5 pb-4 pt-3">
            <div className="pointer-events-none absolute inset-y-0 left-8 flex items-center text-gray-400">
              {icons.search}
            </div>
            <input
              ref={inputRef}
              type="text"
              className="h-12 w-full rounded-xl border border-gray-200/80 bg-white pl-11 pr-10 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              placeholder={
                isSuperadminMode
                  ? 'Buscar usuarios por email, nombre u organización...'
                  : 'Buscar eventos, asistentes, compras o emails...'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveRecentSearch(query);
                }
              }}
            />
            {isLoading && (
              <div className="absolute inset-y-0 right-7 flex items-center">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 px-5 pb-5">
            <div className="flex flex-wrap items-center gap-2">
              {activeFilterOptions.map((option) => {
                const isActive = activeFilter === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => setActiveFilter(option.id)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    type="button"
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
              <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gray-300">
                Ordenar
              </span>
              <div className="relative">
                <select
                  className="h-9 appearance-none rounded-full border border-gray-200 bg-white/90 pl-4 pr-9 text-xs font-semibold text-gray-700 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                >
                  {sortOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-h-[28rem] overflow-y-auto px-5 py-6">
          {hasQuery ? (
            <div className="space-y-6">
              {visibleSections.map((section) => (
                <div key={section.id}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                      {section.label}
                    </p>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                      {section.items.length}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {section.items.map((result) => (
                      <li key={`${result.type}-${result.id}`}>
                        <button
                          onClick={() => handleSelect(result)}
                          className="group flex w-full select-none items-center gap-3 rounded-2xl border border-transparent bg-white/70 p-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/60"
                          type="button"
                        >
                          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-white text-gray-400 shadow-sm ring-1 ring-gray-100 group-hover:text-blue-600">
                            {result.icon}
                          </div>
                          <div className="flex-auto">
                            <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                              {result.title}
                            </p>
                            <p className="text-xs text-gray-500 group-hover:text-blue-600/80">
                              {result.subtitle}
                            </p>
                          </div>
                          <div className="flex-none text-gray-300 transition group-hover:text-blue-400">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {!isLoading && visibleSections.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-300 shadow-sm">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-600">
                    No se encontraron resultados para "{query}"
                  </p>
                  <p className="mt-2 text-xs text-gray-400">
                    Prueba con otra palabra clave o cambia el filtro.
                  </p>
                </div>
              )}

              {isSuperadminMode && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                      Acciones rápidas
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <button
                      onClick={triggerUsersCreate}
                      className="group flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-left shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                      type="button"
                    >
                      <span className="text-blue-600">{icons.user}</span>
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                        Crear
                      </span>
                    </button>
                    <button
                      onClick={triggerUsersExport}
                      className="group flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/80 px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/40"
                      type="button"
                    >
                      <span className="text-gray-500 group-hover:text-blue-600">{icons.download}</span>
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                        Exportar
                      </span>
                    </button>
                    <button
                      onClick={triggerUsersReload}
                      className="group flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/80 px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/40"
                      type="button"
                    >
                      <span className="text-gray-500 group-hover:text-blue-600">{icons.refresh}</span>
                      <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                        Refrescar
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {recentSearches.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                      Búsquedas recientes
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-gray-300">Últimas 3</span>
                      <button
                        onClick={clearRecentSearches}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition hover:border-blue-200 hover:text-blue-600"
                        title="Limpiar búsquedas recientes"
                        type="button"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {recentSearches.map((term) => (
                      <li key={term}>
                        <button
                          onClick={() => {
                            setQuery(term);
                            setActiveFilter('all');
                            inputRef.current?.focus();
                          }}
                          className="group flex w-full items-center gap-3 rounded-2xl border border-gray-100 bg-white/80 px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/60"
                          type="button"
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50 text-gray-400 group-hover:text-blue-600">
                            {icons.search}
                          </div>
                          <div className="flex-auto">
                            <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700">
                              {term}
                            </p>
                          </div>
                          <span className="text-xs font-medium text-gray-300 group-hover:text-blue-400">
                            Buscar
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                    Acciones rápidas
                  </p>
                </div>
                <div className="space-y-2">
                  {isSuperadminMode ? (
                    <>
                      <button
                        onClick={triggerUsersCreate}
                        className="group flex w-full items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-left shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                        type="button"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                          {icons.user}
                        </div>
                        <div className="flex-auto">
                          <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                            Crear usuario
                          </p>
                          <p className="text-xs text-gray-500">Agregar un nuevo usuario al sistema.</p>
                        </div>
                        <div className="flex-none text-blue-300 transition group-hover:text-blue-500">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>

                      <button
                        onClick={triggerUsersExport}
                        className="group flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/40"
                        type="button"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-500 shadow-sm group-hover:text-blue-600">
                          {icons.download}
                        </div>
                        <div className="flex-auto">
                          <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                            Exportar CSV
                          </p>
                          <p className="text-xs text-gray-500">Descargar el listado filtrado.</p>
                        </div>
                      </button>

                      <button
                        onClick={triggerUsersReload}
                        className="group flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/40"
                        type="button"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-500 shadow-sm group-hover:text-blue-600">
                          {icons.refresh}
                        </div>
                        <div className="flex-auto">
                          <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                            Refrescar usuarios
                          </p>
                          <p className="text-xs text-gray-500">Volver a cargar desde el servidor.</p>
                        </div>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/dashboard/events/new');
                      }}
                      className="group flex w-full items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-left shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                      type="button"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                        {icons.event}
                      </div>
                      <div className="flex-auto">
                        <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">
                          Crear nuevo evento
                        </p>
                        <p className="text-xs text-gray-500">Inicia un evento desde cero.</p>
                      </div>
                      <div className="flex-none text-blue-300 transition group-hover:text-blue-500">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
